import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as printerApi from '@/services/printerApi';
import { generateTsplFromLabelData, PRINTER_CONFIGS } from '@/utils/tsplGenerator';
import { fetchComboLabelData } from '@/utils/comboLabelData';

const PRINTER_MODEL = 'TSC_TE210';
// Match V1 ComboAssemblyStation defaults so combo labels render identically.
const ARIAL_LINE_SPACING_MULTIPLIER = 2.5;
const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').replace(/\s*\(\s*/g, '(').replace(/\s*\)\s*/g, ')').trim();

// Combo sticker printing on a local USB printer (product labels, then optional box labels).
export const useComboStickerPrinter = () => {
  const [usbDevice, setUsbDevice] = useState(null);
  const [boxDevice, setBoxDevice] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [progress, setProgress] = useState('');
  const [boxJobs, setBoxJobs] = useState([]);
  const usbSupported = printerApi.isWebUSBSupported();

  useEffect(() => {
    apiClient.get('/main-labels/templates').then((r) => setTemplates(r.data.templates || []))
      .catch((e) => console.error('Failed to load templates', e));
  }, []);

  const findTemplateByName = useCallback((name) => {
    if (!name || !templates.length) return null;
    const t = norm(name);
    return templates.find((x) => norm(x.template_name) === t)
      || templates.find((x) => t.includes(norm(x.template_name)) || norm(x.template_name).includes(t)) || null;
  }, [templates]);

  const connect = async (forBox) => {
    try {
      const device = await printerApi.requestUSBPrinter();
      (forBox ? setBoxDevice : setUsbDevice)(device);
      toast.success(`Connected to ${device.productName || 'USB Printer'}${forBox ? ' (Box Labels)' : ''}`);
      return device;
    } catch (e) {
      console.error('USB connect error:', e);
      toast.error(forBox ? 'Failed to connect box label printer.' : 'Failed to connect USB printer. Please try again.');
      return null;
    }
  };

  const tspl = (job, kind) => {
    const cfg = PRINTER_CONFIGS[PRINTER_MODEL];
    let out = '';
    let count = 0;
    for (const u of job.labelInfo.usns) {
      const cached = job.labelDataMap[u.barcode || job.labelInfo.combo_barcode];
      if (!cached || (kind === 'box' && !u.box_template)) continue;
      const tpl = findTemplateByName(kind === 'box' ? u.box_template : (u.print_template || 'Standard')) || job.defaultTemplate;
      const type = kind === 'box' ? (findTemplateByName(u.box_template)?.template_type || 'box') : tpl?.template_type;
      out += generateTsplFromLabelData(cached.label_data || {}, tpl, u.usn_code, cached.mrp_formatted,
        u.usn_code, cfg, type, u.pair_number, ARIAL_LINE_SPACING_MULTIPLIER);
      count += 1;
    }
    return { out, count };
  };

  // Price check for every session FIRST (a missing rate throws, nothing prints), then print + mark printed.
  const printSessions = async (sessions) => {
    setProgress('Checking prices…');
    try {
      const jobs = [];
      for (const s of sessions) jobs.push({ labelInfo: s, ...(await fetchComboLabelData(s, templates, findTemplateByName)) });
      const device = usbDevice || (await connect(false));
      if (!device) { toast.warning('Printer not connected — nothing printed. Connect it and press Print again.'); return 0; }
      let printed = 0;
      for (const job of jobs) {
        setProgress(`Printing ${job.labelInfo.usn_count} sticker(s) — ${job.labelInfo.combo_name || job.labelInfo.combo_barcode}…`);
        const { out, count } = tspl(job, 'product');
        if (!out) throw new Error('Failed to generate label commands');
        await printerApi.sendToUSBPrinter(device, out);
        await apiClient.post(`/combo/assembly/sessions/${job.labelInfo.session_id}/mark-labels-printed`, {
          session_id: job.labelInfo.session_id, combo_usns: job.labelInfo.usns.map((u) => u.usn_code), print_method: 'USB',
        });
        printed += count;
      }
      const box = jobs.filter((j) => j.labelInfo.has_box_template);
      if (box.length) setBoxJobs(box);
      return printed;
    } finally {
      setProgress('');
    }
  };

  const printBoxLabels = async () => {
    const device = boxDevice || (await connect(true));
    if (!device) return;
    setProgress('Printing box labels…');
    try {
      let n = 0;
      for (const job of boxJobs) {
        const { out, count } = tspl(job, 'box');
        if (out) { await printerApi.sendToUSBPrinter(device, out); n += count; }
      }
      toast.success(n ? `Printed ${n} box label(s)` : 'No box labels to print');
      setBoxJobs([]);
    } catch (e) {
      toast.error(e.message || 'Box label print failed');
    } finally {
      setProgress('');
    }
  };

  return {
    usbDevice, usbSupported, boxDevice, progress, boxJobs,
    connectPrinter: () => connect(false), connectBoxPrinter: () => connect(true),
    printSessions, printBoxLabels, skipBoxLabels: () => setBoxJobs([]),
  };
};
